// src/components/vocabulary/ReviewButtons.jsx
// Anki-style review buttons with time displays
import React, { useState } from 'react';
import { RotateCcw, AlertCircle, Check, Zap } from 'lucide-react';
import { Rating } from '../../services/srsService';
import { useTranslation } from 'react-i18next';
/**
 * Anki-style Review Buttons Component
 * Shows 4 buttons: Again, Hard, Good, Easy with next review times
 */
const ReviewButtons = ({
  onRate,
  buttonLabels,
  disabled = false,
  showKeyboardHints = true,
  isFlipped = false
}) => {
  const { t } = useTranslation();
  const [pressedButton, setPressedButton] = useState(null);

  // Handle button press with animation
  const handleButtonClick = (rating) => {
    if (disabled) return;

    setPressedButton(rating);
    setTimeout(() => setPressedButton(null), 200);

    // Small delay for animation
    setTimeout(() => {
      onRate(rating);
    }, 150);
  };

  // Keyboard shortcuts
  React.useEffect(() => {
    if (disabled) return;

    const handleKeyPress = (e) => {
      switch(e.key) {
        case '1':
          handleButtonClick(Rating.AGAIN);
          break;
        case '2':
          handleButtonClick(Rating.HARD);
          break;
        case '3':
        case ' ': // Spacebar for Good
          e.preventDefault();
          handleButtonClick(Rating.GOOD);
          break;
        case '4':
          handleButtonClick(Rating.EASY);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [disabled]);

  // Button configuration
  const buttons = [
    {
      rating: Rating.AGAIN,
      label: 'Nochmal',
      sublabel: buttonLabels?.again || 'Again (1m)',
      color: 'red',
      icon: RotateCcw,
      keyHint: '1',
      bgClass: 'bg-red-500 hover:bg-red-600 active:bg-red-700',
      shadowClass: 'shadow-red-500/20 hover:shadow-red-500/30',
      borderClass: 'border-red-600'
    },
    {
      rating: Rating.HARD,
      label: 'Schwer',
      sublabel: buttonLabels?.hard || 'Hard (6m)',
      color: 'orange',
      icon: AlertCircle,
      keyHint: '2',
      bgClass: 'bg-orange-500 hover:bg-orange-600 active:bg-orange-700',
      shadowClass: 'shadow-orange-500/20 hover:shadow-orange-500/30',
      borderClass: 'border-orange-600'
    },
    {
      rating: Rating.GOOD,
      label: 'Gut',
      sublabel: buttonLabels?.good || 'Good (10m)',
      color: 'green',
      icon: Check,
      keyHint: '3',
      bgClass: 'bg-green-500 hover:bg-green-600 active:bg-green-700',
      shadowClass: 'shadow-green-500/20 hover:shadow-green-500/30',
      borderClass: 'border-green-600'
    },
    {
      rating: Rating.EASY,
      label: 'Einfach',
      sublabel: buttonLabels?.easy || 'Easy (4d)',
      color: 'blue',
      icon: Zap,
      keyHint: '4',
      bgClass: 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700',
      shadowClass: 'shadow-blue-500/20 hover:shadow-blue-500/30',
      borderClass: 'border-blue-600'
    }
  ];

  return (
    <div className="w-full max-w-2xl mx-auto">
      {/* Button container, 2x2 grid on mobile, single row on desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2 transition-all duration-300">
        {buttons.map((button) => {
          const Icon = button.icon;
          const isPressed = pressedButton === button.rating;

          return (
            <button
              key={button.rating}
              onClick={() => handleButtonClick(button.rating)}
              disabled={disabled}
              className={`
                relative group
                flex flex-col items-center justify-center
                px-3 py-4 sm:px-3 sm:py-3 md:px-4 md:py-4
                min-h-[64px] sm:min-h-[44px]
                rounded-xl sm:rounded-xl md:rounded-2xl
                text-white font-semibold
                transition-all duration-200 transform
                ${button.bgClass}
                ${button.shadowClass}
                shadow-lg sm:shadow-lg hover:shadow-xl
                ${isPressed ? 'scale-95' : 'hover:scale-105 active:scale-95'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                border-b-3 sm:border-b-3 md:border-b-4 ${button.borderClass}
                hover:border-b-2 active:border-b-0
                touch-manipulation
              `}
            >
              {/* Keyboard hint, desktop only */}
              {showKeyboardHints && (
                <div className="hidden sm:flex absolute -top-2 -right-2 w-6 h-6 bg-white text-gray-700 rounded-full text-xs font-bold items-center justify-center shadow-md border border-gray-200">
                  {button.keyHint}
                </div>
              )}

              {/* Icon with animation */}
              <div className={`
                mb-1 sm:mb-0.5 transition-transform duration-200
                ${isPressed ? 'scale-125' : 'group-hover:scale-110'}
              `}>
                <Icon className="w-6 h-6 sm:w-5 sm:h-5 md:w-6 md:h-6" />
              </div>

              {/* Main label */}
              <div className="text-sm sm:text-sm md:text-base font-bold leading-tight">
                {button.label}
              </div>

              {/* Time sublabel */}
              <div className="text-xs sm:text-xs md:text-sm opacity-90 mt-0.5">
                {button.sublabel.match(/\(([^)]+)\)/)?.[1] || '1m'}
              </div>

              {/* Ripple effect on click */}
              {isPressed && (
                <div className="absolute inset-0 rounded-xl sm:rounded-xl overflow-hidden pointer-events-none">
                  <div className="absolute inset-0 bg-white opacity-30 animate-ping" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Keyboard shortcuts hint, desktop only */}
      {showKeyboardHints && (
        <div className="hidden sm:block mt-4 text-center text-xs text-gray-500">
          <span className="inline-flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full">
            <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-300 text-xs">1-4</kbd>
            oder
            <kbd className="px-1.5 py-0.5 bg-white rounded border border-gray-300 text-xs">Space</kbd>
            für Gut
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Compact Review Buttons for mobile or limited space
 */
export const CompactReviewButtons = ({
  onRate,
  buttonLabels,
  disabled = false,
  isFlipped = false
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex gap-2 w-full transition-opacity duration-300">
      <button
        onClick={() => onRate(Rating.AGAIN)}
        disabled={disabled}
        className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 px-2 rounded-lg font-medium text-sm transition-all hover:scale-105 active:scale-95"
      >
        <RotateCcw className="w-4 h-4 mx-auto mb-1" />
        Nochmal
      </button>

      <button
        onClick={() => onRate(Rating.HARD)}
        disabled={disabled}
        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white py-3 px-2 rounded-lg font-medium text-sm transition-all hover:scale-105 active:scale-95"
      >
        <AlertCircle className="w-4 h-4 mx-auto mb-1" />
        Schwer
      </button>

      <button
        onClick={() => onRate(Rating.GOOD)}
        disabled={disabled}
        className="flex-1 bg-green-500 hover:bg-green-600 text-white py-3 px-2 rounded-lg font-medium text-sm transition-all hover:scale-105 active:scale-95"
      >
        <Check className="w-4 h-4 mx-auto mb-1" />
        Gut
      </button>

      <button
        onClick={() => onRate(Rating.EASY)}
        disabled={disabled}
        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 px-2 rounded-lg font-medium text-sm transition-all hover:scale-105 active:scale-95"
      >
        <Zap className="w-4 h-4 mx-auto mb-1" />
        Einfach
      </button>
    </div>
  );
};

export default ReviewButtons;